import { createContext, use, useCallback, useEffect, useMemo, useState } from 'react';
import type { PublicUser } from '@gc/shared';
import { ApiError, apiRequest, setCsrfToken } from '../../api/client.js';

type SessionState =
  | { readonly estado: 'comprobando' }
  | { readonly estado: 'anonimo' }
  | { readonly estado: 'autenticado'; readonly usuario: PublicUser };

type SessionContextValue = {
  readonly sesion: SessionState;
  registrar(email: string, password: string): Promise<void>;
  iniciarSesion(email: string, password: string): Promise<void>;
  cerrarSesion(): Promise<void>;
};

const SessionContext = createContext<SessionContextValue | null>(null);

/** Tras autenticarse hay que pedir el token CSRF: toda mutación posterior lo exige. */
async function cargarCsrf(): Promise<void> {
  const { csrfToken } = await apiRequest<{ csrfToken: string }>('/auth/csrf');
  setCsrfToken(csrfToken);
}

export function SessionProvider({ children }: { children: React.ReactNode }): React.JSX.Element {
  const [sesion, setSesion] = useState<SessionState>({ estado: 'comprobando' });

  useEffect(() => {
    let cancelado = false;
    // Al cargar la página la cookie puede seguir viva: se comprueba contra la API.
    apiRequest<PublicUser>('/auth/me')
      .then(async (usuario) => {
        await cargarCsrf();
        if (!cancelado) setSesion({ estado: 'autenticado', usuario });
      })
      .catch(() => {
        if (!cancelado) setSesion({ estado: 'anonimo' });
      });
    return () => {
      cancelado = true;
    };
  }, []);

  const autenticar = useCallback(async (ruta: string, email: string, password: string) => {
    const usuario = await apiRequest<PublicUser>(ruta, { method: 'POST', body: { email, password } });
    await cargarCsrf();
    setSesion({ estado: 'autenticado', usuario });
  }, []);

  const valor = useMemo<SessionContextValue>(
    () => ({
      sesion,
      registrar: (email, password) => autenticar('/auth/register', email, password),
      iniciarSesion: (email, password) => autenticar('/auth/login', email, password),
      async cerrarSesion() {
        try {
          await apiRequest<void>('/auth/logout', { method: 'POST' });
        } catch (error) {
          // Una sesión ya caducada en el servidor no debe dejar la UI bloqueada.
          if (!(error instanceof ApiError) || error.status < 400) throw error;
        } finally {
          setCsrfToken(undefined);
          setSesion({ estado: 'anonimo' });
        }
      },
    }),
    [sesion, autenticar],
  );

  return <SessionContext value={valor}>{children}</SessionContext>;
}

export function useSession(): SessionContextValue {
  const contexto = use(SessionContext);
  if (!contexto) throw new Error('useSession debe usarse dentro de SessionProvider');
  return contexto;
}
